import { resolveMediaUrl } from './mediaUrl';

const getSection = (page, key) => page?.sections?.find((section) => section.key === key) || null;

const getMediaUrl = (section, groupKey) => {
  const media =
    section?.media?.groups?.[groupKey]?.[0] ||
    (groupKey === 'hero-image' || groupKey === 'feature-image' || groupKey === 'check-icon' || groupKey === 'note-icon'
      ? section?.media?.primary
      : null);

  return media?.url ? resolveMediaUrl(media.url) : '';
};

export const parseVpsHostingHeroContent = (page) => {
  const section = getSection(page, 'hero');
  if (!section) {
    return null;
  }

  const fields = section.fields || {};
  if (!fields.title) {
    return null;
  }

  return {
    eyebrow: fields.eyebrow || '',
    title: fields.title,
    text: fields.text || '',
    cta: {
      text: fields.ctaText || '',
      url: fields.ctaUrl || '/contact',
    },
    image: getMediaUrl(section, 'hero-image'),
  };
};

export const parseVpsHostingDedicatedContent = (page) => {
  const section = getSection(page, 'dedicated');
  if (!section) {
    return null;
  }

  const fields = section.fields || {};
  if (!fields.title) {
    return null;
  }

  const paragraphs = [];
  for (let index = 1; index <= 10; index += 1) {
    const value = fields[`paragraph-${index}`];
    if (!value) {
      break;
    }
    paragraphs.push(value);
  }

  return {
    title: fields.title,
    paragraphs,
    image: getMediaUrl(section, 'feature-image'),
  };
};

export const parseVpsHostingWhyClientsContent = (page) => {
  const section = getSection(page, 'why-clients');
  if (!section) {
    return null;
  }

  const fields = section.fields || {};
  const items = [];

  for (let index = 1; index <= 20; index += 1) {
    const title = fields[`item-${index}-title`];
    if (!title) {
      break;
    }

    items.push({
      title,
      text: fields[`item-${index}-text`] || '',
      icon: getMediaUrl(section, `item-${index}-icon`),
    });
  }

  if (items.length === 0) {
    return null;
  }

  return {
    title: fields.title || 'Why Clients Choose VPS.',
    items,
  };
};

export const parseVpsHostingPackagesContent = (page) => {
  const section = getSection(page, 'packages');
  if (!section) {
    return null;
  }

  const fields = section.fields || {};
  if (!fields.title) {
    return null;
  }

  const packages = [];

  for (let index = 1; index <= 10; index += 1) {
    const name = fields[`package-${index}-name`];
    if (!name) {
      break;
    }

    const features = [];
    for (let featureIndex = 1; featureIndex <= 10; featureIndex += 1) {
      const feature = fields[`package-${index}-feature-${featureIndex}`];
      if (!feature) {
        break;
      }
      features.push(feature);
    }

    packages.push({
      price: fields[`package-${index}-price`] || '',
      name,
      description: fields[`package-${index}-description`] || '',
      cta: fields[`package-${index}-cta`] || 'Select',
      features,
    });
  }

  if (packages.length === 0) {
    return null;
  }

  return {
    eyebrow: fields.eyebrow || '',
    title: fields.title,
    intro: fields.intro || '',
    checkIcon: getMediaUrl(section, 'check-icon'),
    packages,
  };
};

export const parseVpsHostingIncludedContent = (page) => {
  const section = getSection(page, 'included');
  if (!section) {
    return null;
  }

  const fields = section.fields || {};
  const items = [];

  for (let index = 1; index <= 20; index += 1) {
    const text = fields[`item-${index}-text`];
    if (!text) {
      break;
    }
    items.push(text);
  }

  if (items.length === 0) {
    return null;
  }

  return {
    title: fields.title || 'Included With Every VPS.',
    checkIcon: getMediaUrl(section, 'check-icon'),
    items,
  };
};

export const parseVpsHostingRequestToResponseContent = (page) => {
  const section = getSection(page, 'request-to-response');
  if (!section) {
    return null;
  }

  const fields = section.fields || {};
  const steps = [];

  for (let index = 1; index <= 20; index += 1) {
    const title = fields[`step-${index}-title`];
    if (!title) {
      break;
    }

    steps.push({
      number: fields[`step-${index}-number`] || `${index}.`,
      title,
      text: fields[`step-${index}-text`] || '',
    });
  }

  if (steps.length === 0) {
    return null;
  }

  return {
    title: fields.title || '',
    steps,
  };
};

export const parseVpsHostingAccessControlContent = (page) => {
  const section = getSection(page, 'access-control');
  if (!section) {
    return null;
  }

  const fields = section.fields || {};
  if (!fields.title) {
    return null;
  }

  return {
    title: fields.title,
    text: fields.text || '',
    note: fields.note || '',
    image: getMediaUrl(section, 'feature-image'),
    noteIcon: getMediaUrl(section, 'note-icon'),
  };
};

export const parseVpsHostingFaqContent = (page) => {
  const section = getSection(page, 'faq');
  if (!section) {
    return null;
  }

  const fields = section.fields || {};
  const items = [];

  const faqIndexes = new Set();
  Object.keys(fields).forEach((key) => {
    const match = key.match(/^faq-(\d+)-question$/);
    if (match) {
      faqIndexes.add(Number(match[1]));
    }
  });

  [...faqIndexes].sort((a, b) => a - b).forEach((index) => {
    const question = fields[`faq-${index}-question`];
    if (!question) {
      return;
    }

    items.push({
      question,
      answer: fields[`faq-${index}-answer`] || '',
    });
  });

  if (items.length === 0 && !fields.title) {
    return null;
  }

  return {
    title: fields.title || 'Common Questions.',
    subtitle: fields.subtitle || '',
    items,
  };
};

export const parseVpsHostingContinuityContent = (page) => {
  const section = getSection(page, 'continuity');
  if (!section) {
    return null;
  }

  const fields = section.fields || {};
  if (!fields.title) {
    return null;
  }

  return {
    title: fields.title,
    text: fields.text || '',
    cta: {
      text: fields.ctaText || '',
      url: fields.ctaUrl || '/technology/vps-hosting/request',
    },
  };
};
